import React, { useMemo } from "react";
import { Image, Layer, Stage } from "react-konva";
import { FloorAnalyticsView } from "src/core/enums/floor-analytics-enums";
import { FloorShape } from "src/shared/models/floorPlan";
import ShapreLoaderComponent from "./shapre-loader.component";
import AssetRendererComponent from "./asset-renderer.component";
import { Layout } from "src/shared/helpers/label-layout.helper";
import { calculateImageDimensions } from "src/shared/helpers/floorplan.helpers";

interface FloorplanCanvasComponentProps {
  containerDimensions: { width: number; height: number };
  zoomLevel: number;
  onWheel: (e: any) => void;
  onTouchStart?: (e: any) => void;
  onTouchMove?: (e: any) => void;
  onTouchEnd?: (e: any) => void;
  onDragEnd: (x: number, y: number) => void;
  selectedFile: any;
  isLoading: boolean;
  shapes: FloorShape[];
  showLabels: boolean;
  selectedViewType: FloorAnalyticsView;
  selectedAssetList: string[];
  selectedAssetOrder: Record<string, number>;
  selectedSearchedAssetCodeForHighlighting: string;
  gatewayImageFile: any;
  photocellImageFileOffline: any;
  selectedAssetImageFile: any;
  labelLayouts: Map<string | number, Layout>;
  dynamicValues: {
    circle: number;
    circlePositionOffset: number;
    gateway: {
      size: number;
      offset: number;
      labelOffset: number;
    };
    photocell: {
      size: number;
      offset: number;
      labelOffset: number;
    };
  };
  labelDynamics: {
    fontSize: number;
    height: number;
    yOffset: number;
    padding: number;
    textPadding: number;
    strokeWidth: number;
    cornerRadius: number;
    shadowBlur: number;
    shadowColor: number;
    shadowOffset: number;
  };
  heatmapMotionDataMap?: Map<string, (number | null)[]>;
  lightOPStatusDataMap?: Map<string, (number | null)[]>;
  lightIntensityDataMap?: Map<string, (number | null)[]>;
  hourRange: number;
  getHeatmapColor?: (
    motionOrIntensityCount: number | null,
    viewType?: string,
    maxValue?: number,
    lightStatusValue?: number | null,
  ) => string;
  // getLightOPStatusColor?: (lightOPStatus: number | null) => string;
  onAssetClick: (id: string, e?: any) => void;
  onAssetMouseEnter: (e: any, shape: FloorShape) => void;
  onAssetMouseLeave: (e: any) => void;
  onClickCanvas: () => void;
  stageRef: React.RefObject<any>;
}

const FloorplanCanvasComponent: React.FC<FloorplanCanvasComponentProps> = ({
  containerDimensions,
  zoomLevel,
  onWheel,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onDragEnd,
  selectedFile,
  isLoading,
  shapes,
  showLabels,
  selectedViewType,
  selectedAssetList,
  selectedAssetOrder,
  selectedSearchedAssetCodeForHighlighting,
  gatewayImageFile,
  photocellImageFileOffline,
  selectedAssetImageFile,
  labelLayouts,
  dynamicValues,
  labelDynamics,
  heatmapMotionDataMap,
  lightOPStatusDataMap,
  lightIntensityDataMap,
  hourRange,
  getHeatmapColor,
  // getLightOPStatusColor,
  onAssetClick,
  onAssetMouseEnter,
  onAssetMouseLeave,
  onClickCanvas,
  stageRef,
}) => {

  // handle image when height is more than width
  const { width, height, assetScale } = useMemo(
    () => calculateImageDimensions(selectedFile),
    [selectedFile]
  );

  return (
    <Stage
      width={containerDimensions.width}
      height={containerDimensions.height}
      scaleX={zoomLevel}
      scaleY={zoomLevel}
      ref={stageRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
      className="my-3 stage-wrapper"
      onClick={onClickCanvas}
    >
      <Layer
        y={15}
        draggable
        isLayer={true}
        onDragEnd={(e) => {
          const target = e.target;
          if (target.attrs.isLayer) {
            onDragEnd(e.target.x(), e.target.y());
          }
        }}
      >
        {selectedFile && containerDimensions?.height > 0 && (
          <Image
            name="floor-image"
            image={selectedFile}
            width={width}
            height={height}
          />
        )}

        {isLoading ? (
          <ShapreLoaderComponent
            containerWidth={containerDimensions.width}
            containerHeight={containerDimensions.height}
          />
        ) : (
          shapes?.map((shape: FloorShape) => (
            <AssetRendererComponent
              key={shape.id}
              shape={shape}
              isGateway={shape?.parentAssetTypeId?.toLowerCase() === "3"} // Gateway
              isPhotocell={
                shape?.parentAssetTypeId?.toLowerCase() === "1" || shape?.parentAssetTypeId?.toLowerCase() === "44" // Photocell Sensor
              }
              isHighlighted={
                shape?.assetCode === selectedSearchedAssetCodeForHighlighting
              }
              isSelected={selectedAssetList.includes(shape?.assetCode)}
              selectedAssetOrder={selectedAssetOrder}
              viewType={selectedViewType}
              heatmapColor={
                selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
                  getHeatmapColor &&
                  heatmapMotionDataMap
                  ? getHeatmapColor(
                    heatmapMotionDataMap.get(shape?.assetCode)?.[hourRange] ??
                    null,
                  )
                  : undefined
              }
              heatmapValue={
                selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
                  heatmapMotionDataMap
                  ? heatmapMotionDataMap.get(shape?.assetCode)?.[hourRange] ??
                  null
                  : undefined
              }
              lightOPStatusColor={
                selectedViewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS &&
                  getHeatmapColor &&
                  lightOPStatusDataMap
                  ? getHeatmapColor(
                    null,
                    FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS,
                    100,
                    lightOPStatusDataMap.get(shape?.assetCode)?.[hourRange] ??
                    null,
                  )
                  : undefined
              }
              gatewayImageFile={gatewayImageFile}
              photocellImageFileOffline={photocellImageFileOffline}
              selectedAssetImageFile={selectedAssetImageFile}
              dynamicCircle={dynamicValues.circle}
              dynamicCirclePositionOffset={dynamicValues.circlePositionOffset}
              dynamicGatewaySize={dynamicValues.gateway.size}
              dynamicGatewayOffset={dynamicValues.gateway.offset}
              dynamicGatewayLabelOffset={dynamicValues.gateway.labelOffset}
              dynamicPhotocellSize={dynamicValues.photocell.size}
              dynamicPhotocellOffset={dynamicValues.photocell.offset}
              dynamicPhotocellLabelOffset={dynamicValues.photocell.labelOffset}
              showLabels={showLabels}
              labelLayout={labelLayouts.get(shape.id)}
              labelDynamics={labelDynamics}
              zoomLevel={zoomLevel}
              onAssetClick={onAssetClick}
              onAssetMouseEnter={(e) => onAssetMouseEnter(e, shape)}
              onAssetMouseLeave={onAssetMouseLeave}
              assetScale={assetScale}
            />
          ))
        )}
      </Layer>
    </Stage>
  );
};

export default FloorplanCanvasComponent;
